//헤더 컴포넌트
import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { palette } from "../../../../assets/styles/Palette";
import { Sub1 } from "../../../../assets/styles/Typography";
import { useAtom } from "jotai";
import {
  PERSONA_STEP,
  USER_CREDITS,
  IS_LOGGED_IN,
} from "../../../AtomStates";
import { useDynamicViewport } from "../../../../assets/DynamicViewport";
import PopupWrap from "../../../../assets/styles/Popup";

const MoleculeCreateDisplayHeader = ({
  activeTab,
  setActiveTab,
  completedSteps,
  isLoading,
  isLoadingReport,
  onPreview,
  onSave,
  onSubmitConcept, // 1단계: handleSubmitConcept
  onSubmitReport, // 2단계: handleSubmitReport
  onDisplayConversion, // 3단계: handleDisplayConversion
  hasContentDraft,
  onConfirmContent,
  isLoadingTemplate,
  canProceedFromStep1, // 1단계에서 다음 버튼 활성화 조건
  canProceedFromStep2, // 2단계에서 다음 버튼 활성화 조건
  canProceedFromStep3,
  isSerialEntered,
  customSteps = null, // 커스텀 단계 구조 [{ number: 1, label: "단계명", activeTab: 1 }]
}) => {
  useDynamicViewport("width=1280"); // 특정페이지에서만 pc화면처럼 보이기
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedInState, setIsLoggedInState] = useState(false);
  const [isLoggedIn] = useAtom(IS_LOGGED_IN);
  const [showExitPopup, setShowExitPopup] = useState(false);


  const handlePrevious = () => {
    // 커스텀 단계 구조인 경우 첫 단계에서는 대시보드로 이동
    if (customSteps && activeTab === customSteps[0].activeTab) {
      navigate("/EduInfoContentManageDashBoard");
      return;
    }
    // 첫 번째 탭에서는 콘텐츠 리스트로 이동 (취소 기능), 다른 탭에서는 이전 탭으로 이동
    if (activeTab === 1) {
      // navigate("/EducationDashBoard?view=contentsList");
      navigate("/EduInfoContentManageDashBoard")
    } else if (activeTab > 1) {
      setActiveTab(activeTab - 1);
    }
  };

  const handleNext = () => {
    // 다음 탭으로 이동하는 로직 (기존 4단계가 3단계로 변경됨)
    if (activeTab < 3) {
      setActiveTab(activeTab + 1);
    }
  };

  const getButtonText = () => {
    // 커스텀 단계 구조인 경우
    if (customSteps) {
      const isLastStep = activeTab === customSteps[customSteps.length - 1].activeTab;
      return isLastStep ? "확정" : "다음";
    }
    // 기본 단계 구조
    switch (activeTab) {
      case 1:
        return "다음";
      case 2:
        return hasContentDraft ? "다음" : "다음";
      case 3:
        return "다음";
      default:
        return "다음";
    }
  };

  const handleActionClick = async () => {
    // 커스텀 단계 구조인 경우
    if (customSteps) {
      const currentStep = customSteps.find(step => step.activeTab === activeTab);
      if (currentStep) {
        // 마지막 단계인 경우 확정 처리
        if (activeTab === customSteps[customSteps.length - 1].activeTab) {
          onConfirmContent && onConfirmContent();
        } else {
          // 첫 번째 단계인 경우 onSubmitConcept 호출 (서버 저장)
          if (activeTab === customSteps[0].activeTab) {
            if (onSubmitConcept) {
              try {
                await onSubmitConcept();
              } catch (error) {
                // 에러 발생 시 탭 이동하지 않음
                console.error("콘텐츠 저장 실패:", error);
                return;
              }
            } else {
              // onSubmitConcept이 없으면 단순히 다음 단계로 이동
              const nextStep = customSteps.find(step => step.activeTab > activeTab);
              if (nextStep) {
                setActiveTab(nextStep.activeTab);
              }
            }
          } else {
            // 다음 단계로 이동
            const nextStep = customSteps.find(step => step.activeTab > activeTab);
            if (nextStep) {
              setActiveTab(nextStep.activeTab);
            }
          }
        }
      }
      return;
    }
    
    // 기본 단계 구조
    switch (activeTab) {
      case 1:
        onSubmitConcept && onSubmitConcept();
        break;
      // 기존 2단계는 주석처리됨
      // case 2:
      //   onSubmitReport && onSubmitReport();
      //   break;
      case 2:
        // 기존 3단계가 2단계로 변경됨
        if (hasContentDraft) {
          // 콘텐츠 시안이 있는 경우 다음 탭으로 이동
          onConfirmContent && onConfirmContent();
        } else {
          // 콘텐츠 시안이 없는 경우 디스플레이 변환 실행
          onDisplayConversion && onDisplayConversion();
        }
        break;
      case 3:
        // 기존 4단계가 3단계로 변경됨
        navigate("/EduInfoContentManageDashBoard");
        break;
      default:
        onSave && onSave();
    }
  };

  const handleExitCancel = () => {
    setShowExitPopup(false);
  };

  const handleExitConfirm = () => {
    setShowExitPopup(false);
    navigate("/EduInfoContentManageDashBoard");
  };

  return (
    <>
      <HeaderWrap>
        {/* 왼쪽: 취소하기 */}
        <LeftSection>
          <LogoContainer onClick={() => setShowExitPopup(true)}>
            <Sub1 color="gray800" style={{ fontWeight: "600" }}>
              Autodisplay
            </Sub1>
          </LogoContainer>
        </LeftSection>

        {/* 중앙: 탭 버튼들 */}
        <CenterSection>
          <TabContainer>
            {customSteps ? (
                // 커스텀 단계 구조 사용
                customSteps.map((step, index) => (
                  <React.Fragment key={step.number}>
                    {index > 0 && <TabDivider />}
                    <TabButton
                      isActive={activeTab >= step.activeTab}
                      onClick={() => {
                        // completedSteps 체크가 필요한 경우
                        if (step.requiredCompletedStep && !completedSteps.includes(step.requiredCompletedStep)) {
                          return;
                        }
                        setActiveTab(step.activeTab);
                      }}
                      disabled={
                        (step.requiredCompletedStep && !completedSteps.includes(step.requiredCompletedStep)) ||
                        isLoading ||
                        isLoadingReport ||
                        isLoadingTemplate
                      }
                    >
                      <TabNumber isActive={activeTab >= step.activeTab}>{step.number}</TabNumber>
                      <TabText isActive={activeTab >= step.activeTab}>{step.label}</TabText>
                    </TabButton>
                  </React.Fragment>
                ))
              ) : (
                // 기본 단계 구조
                <>
                  <TabButton
                    isActive={activeTab >= 1}
                    onClick={() => setActiveTab(1)}
                    disabled={isLoading || isLoadingReport || isLoadingTemplate}
                  >
                    <TabNumber isActive={activeTab >= 1}>1</TabNumber>
                    <TabText isActive={activeTab >= 1}>자료 업로드</TabText>
                  </TabButton>

                  {/* 2단계 주석처리 */}
                  {/* <TabDivider />

                  <TabButton
                    isActive={activeTab >= 2}
                    onClick={() => completedSteps.includes(1) && setActiveTab(2)}
                    disabled={
                      !completedSteps.includes(1) ||
                      isLoading ||
                      isLoadingReport ||
                      isLoadingTemplate
                    }
                  >
                    <TabNumber isActive={activeTab >= 2}>2</TabNumber>
                    <TabText isActive={activeTab >= 2}>자료 분석</TabText>
                  </TabButton> */}

                  <TabDivider />

                  {/* 기존 3단계를 2단계로 변경 */}
                  <TabButton
                    isActive={activeTab >= 2}
                    onClick={() =>
                      (completedSteps.includes(1) || completedSteps.includes(3)) &&
                      setActiveTab(2)
                    }
                    disabled={
                      !completedSteps.includes(1) ||
                      isLoading ||
                      isLoadingReport ||
                      isLoadingTemplate
                    }
                  >
                    <TabNumber isActive={activeTab >= 2}>2</TabNumber>
                    <TabText isActive={activeTab >= 2}>콘텐츠 생성</TabText>
                  </TabButton>

                  <TabDivider />

                  {/* 기존 4단계를 3단계로 변경 */}
                  <TabButton
                    isActive={activeTab >= 3}
                    onClick={() =>
                      completedSteps.includes(4) &&
                      setActiveTab(3)
                    }
                    disabled={
                      !completedSteps.includes(4) ||
                      isLoading ||
                      isLoadingReport ||
                      isLoadingTemplate
                    }
                  >
                    <TabNumber isActive={activeTab >= 3}>3</TabNumber>
                    <TabText isActive={activeTab >= 3}>화면 실행</TabText>
                  </TabButton>
                </>
              )}
          </TabContainer>
        </CenterSection>
      </HeaderWrap>

      {/* 하단 버튼 영역 - activeTab이 3이 아닐 때만 표시 (기존 4단계가 3단계로 변경됨) */}
      {/* 커스텀 단계 구조인 경우 마지막 단계에서는 버튼 숨김 */}
      {((customSteps && activeTab !== customSteps[customSteps.length - 1].activeTab) || (!customSteps && activeTab !== 3)) && (
        <BottomButtonContainer>
          <BottomButtonWrapper>
            <BackButton
              onClick={handlePrevious}
              disabled={isLoading || isLoadingReport || isLoadingTemplate}
            >
              <span>뒤로</span>
            </BackButton>
            <NextButton
              onClick={handleActionClick}
              disabled={
                isLoading ||
                isLoadingReport ||
                isLoadingTemplate ||
                (customSteps 
                  ? (activeTab === customSteps[0].activeTab && !canProceedFromStep1) ||
                    (activeTab === customSteps[customSteps.length - 1].activeTab && !canProceedFromStep3)
                  : (activeTab === 1 && !canProceedFromStep1) ||
                    (activeTab === 2 && !canProceedFromStep3) // 기존 3단계가 2단계로 변경됨
                )
              }
            >
              <span>{getButtonText()}</span>
            </NextButton>
          </BottomButtonWrapper>
        </BottomButtonContainer>
      )}

      {showExitPopup && (
        <PopupWrap
          Warning={true}
          title="정말 종료하시겠습니까?"
          message="지금 종료하면 진행 중인 내용이 모두 삭제됩니다. "
          buttonType="Outline"
          closeText="취소"
          confirmText="확인"
          isModal={false}
          onCancel={handleExitCancel}
          onConfirm={handleExitConfirm}
        />
      )}
    </>
  );
};

export default MoleculeCreateDisplayHeader;

const HeaderWrap = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 53.75px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 28px;
  border-bottom: 1px solid ${palette.lineGray};
  background: ${palette.white};
  z-index: 99;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const CancelButton = styled.div`
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const CenterSection = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
`;

const TabContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
`;

const TabDivider = styled.div`
  width: 20px;
  height: 1px;
  background: ${palette.gray300};
  margin: 0 8px;
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 20px;
  border: none;
  background: transparent;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: all 0.2s ease;

  ${(props) =>
    props.isActive
      ? css`
          // background: ${palette.gray800};
        `
      : css`
          background: transparent;
          // border: 1px solid ${palette.gray300};
        `}

  &:hover {
    opacity: ${(props) => (props.disabled ? 0.5 : 0.8)};
  }
`;

const TabNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 20%;
  font-family: Poppins;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;

  ${(props) =>
    props.isActive
      ? css`
          background: ${palette.gray800};
          color: ${palette.white};
        `
      : css`
          // background: ${palette.gray400};
          color: ${palette.gray800};
          border: 1px solid ${palette.gray300};
        `}
`;

const TabText = styled.div`
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: Pretendard;

  ${(props) =>
    props.isActive
      ? css`
          color: ${palette.gray800};
          font-weight: 600;
        `
      : css`
          color: ${palette.gray500};
          font-weight: 600;
        `}
`;

const BottomButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 0px;
  margin-bottom: 0px;
  z-index: 100;
`;

const BottomButtonWrapper = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 0 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e0e4eb;
  padding-top: 16px;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  height: 40px;
  min-width: 98px;
  padding: 0 0px;
  background: transparent;
  border: 1px solid #b3b7ba;
  border-radius: 8px;
  cursor: pointer;

  span {
    color: #464c53;
    font-size: 16px;
    font-family: Pretendard;
    font-weight: 700;
    line-height: 28.5px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: rgba(179, 183, 186, 0.1);
  }
`;

const NextButton = styled.button`
  height: 40px;
  min-width: 98px;
  padding: 0 24px;
  background: #256ef4;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  span {
    color: white;
    font-size: 16px;
    font-family: Pretendard;
    font-weight: 700;
    line-height: 28.5px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #b3b7ba;
  }

  &:hover:not(:disabled) {
    background: #0b45b1;
  }
`;
const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  height: 53.75px;
  cursor: pointer; // 클릭 가능하다는 표시
`;
